import {
  IAllowedActionScope,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IDeleteActionScope,
  IMoveActionScope,
  RefreshContentScope,
} from '../../app/ActionPlaceScopes';
import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../app/actions/spaces/FavoritesActions';
import { appStore } from '../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs, watchUrlForBreadcrumbUpdates
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import {
  DetailsDialogInAngularPlaceHelper,
  IDetailsDialogInAngularPlaceHelper
} from '../../app/spaces/DetailsDialog/AngularContentDetailsDialog';
import { addUrlWatchersToScopeForExplorer } from '../../app/spaces/Explorer/ExplorerUrlBindings';
import { ProjectSpaceOwnProps } from '../../app/spaces/ProjectSpace/ProjectSpace';
import { setURLParametersOrDefaultsForEnteringSpaceAndFetchContent } from '../../app/spaces/SpaceConfig/SiteSpaceConfigHelper';
import { getQueryStringServiceFromAngular } from '../../common/react/QueryString';
import { AngularContext, AngularContextFactory } from '../factories/AngularContextFactory';
import { IActionsMenuScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { ISubplaceTabsScope } from '../helpers/SubplaceCountsHelper';
import { IPlace } from '../models/IPlace';
import { IProjectDetailInfo, IProjectInfo, ProjectDetailWithUsageInfo } from '../models/IProjectInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IProjectActions } from '../ServerApi/types/IProjectActions';
import { ActionPanelService } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeProjectDescriptionAction } from '../services/ChangeProjectDescriptionAction';
import { ContentActions } from '../services/ContentActions';
import { ModalService } from '../services/ModalService';
import { ServerService } from '../services/ServerService';
import { Telemetry } from '../services/TelemetryService';

export interface IProjectPlaceScope extends
  IActionsMenuScope<IProjectInfo, IProjectDetailInfo>,
  IAllowedActionScope<IProjectActions>,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IDeleteActionScope,
  IMoveActionScope,
  IPlaceScope,
  RefreshContentScope,
  ISubplaceTabsScope
{
  project: IProjectDetailInfo;
  projectSpaceProps: ProjectSpaceOwnProps;
  flowsOnSiteEnabled: boolean;
}

export function isProjectPlaceScope(scope: IPlaceScope): scope is IProjectPlaceScope {
  return scope.place && scope.place.name === 'project';
}

export class ProjectPlaceCtrl {

  private currentPath: string;

  public static $inject = [
    '$location',
    '$q',
    '$scope',
    '$state',
    '$translate',
    'AngularContextFactory',
    'ActionPanelService',
    'BrowserTitleService',
    'ChangeProjectDescriptionAction',
    'ContentActions',
    'projectActions',
    'projectDetailInfo',
    'projectHierarchy',
    'projectInfo',
    '$sanitize',
    'ModalService',
    'ServerService',
    'TelemetryService',
  ];

  constructor(
    $location: ng.ILocationService,
    $q: ng.IQService,
    $scope: IProjectPlaceScope,
    $state: ng.ui.IStateService,
    $translate: ng.translate.ITranslateService,
    angularContextFactory: AngularContextFactory,
    actionPanelService: ActionPanelService,
    browserTitleService: BrowserTitleService,
    ChangeProjectDescriptionAction: ChangeProjectDescriptionAction,
    ContentActions: ContentActions,
    projectActions: IProjectActions,
    projectDetailInfo: ProjectDetailWithUsageInfo,
    projectHierarchy: INamedItemInfo[],
    projectInfo: IProjectInfo,
    $sanitize: ng.sanitize.ISanitizeService,
    modalService: ModalService,
    server: ServerService,
    TelemetryService: Telemetry,
  ) {
    $scope.refreshContent = () => $state.hardReload();

    $scope.flowsOnSiteEnabled = server.flowsOnSiteEnabled();

    const angularContext = angularContextFactory.create($scope);

    const spacesSiteUrls = siteUrlsSelector(appStore.getState());
    populateContentBreadcrumbs(addContentTypeToProjectHierarchy(projectHierarchy), spacesSiteUrls);

    watchUrlForBreadcrumbUpdates($scope, $location, () => {
      return this.currentPath === $location.path();
    });

    browserTitleService.setTitleWithPlaceEntityName({placeEntityName: projectDetailInfo.name});

    const detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        $scope.refreshContent,
        $q,
        angularContext,
        modalService,
        TelemetryService
    );

    const queryString = getQueryStringServiceFromAngular($location);

    const projectDetailWithUsageInfo: ProjectDetailWithUsageInfo = {
      usageInfo: projectInfo.usageInfo,
      ...projectDetailInfo
    };

    setUpProjectSpaceProps(
        $scope,
        $sanitize,
        TelemetryService,
        projectDetailWithUsageInfo,
        projectHierarchy,
        angularContext,
        detailsDialogHelper,
    );
    this.currentPath = $location.path();
    const getPath = () => {
      return this.currentPath;
    };

    initNecessaryScopePropertiesForDetailsPane(
        $scope,
        $state,
        projectDetailInfo,
        projectActions,
        ChangeProjectDescriptionAction,
        ContentActions,
        detailsDialogHelper,
    );

    // Need to reset all the filters and sorts when navigating in to a project
    setURLParametersOrDefaultsForEnteringSpaceAndFetchContent(appStore, $location);
    addUrlWatchersToScopeForExplorer({ $scope, $location, queryString, store: appStore, getPreviousPath: getPath });
  }
}

function initNecessaryScopePropertiesForDetailsPane(
    $scope: IProjectPlaceScope,
    $state: ng.ui.IStateService,
    projectDetailInfo: IProjectDetailInfo,
    projectActions: IProjectActions,
    ChangeProjectDescriptionAction: ChangeProjectDescriptionAction,
    ContentActions: ContentActions,
    dialogHelper: IDetailsDialogInAngularPlaceHelper,
) {

  $scope.project = projectDetailInfo;

  $scope.allowedAction = (actionName: keyof IProjectActions) => (!!projectActions && projectActions[actionName]);

  $scope.del = ($event: JQueryEventObject) => {
    ContentActions.deleteProject(projectDetailInfo, ActionContext.fromEvent($event))
    .then(() => $state.go('main.site.content.site.projects'));
  };

  $scope.changeDescription = (newDescription: string) => {
    return ChangeProjectDescriptionAction.execute(projectDetailInfo, newDescription)
      .then(dialogHelper.inlineEditResultHandler);
  };

  $scope.changeOwner = ($event: JQueryEventObject) => {
    const projectSet = Set.singletonForIdItem(projectDetailInfo);
    const actionContext = ActionContext.fromEvent($event);
    return ContentActions.changeOwnerOnProjects(projectSet, actionContext)
      .then(dialogHelper.actionFromSeparateDialogResultHandler);
  };
}

function setUpProjectSpaceProps(
    $scope: IProjectPlaceScope,
    $sanitize: ng.sanitize.ISanitizeService,
    telemetryService: Telemetry,
    projectDetailInfo: ProjectDetailWithUsageInfo,
    projectHierarchy: INamedItemInfo[],
    angularContext: AngularContext,
    detailsDialogHelper: IDetailsDialogInAngularPlaceHelper,
) {
  $scope.projectSpaceProps = {
    angularContext: angularContext,
    projectDetail: projectDetailInfo,
    projectId: projectDetailInfo.id,
    openDetail: () => detailsDialogHelper.openDialog(projectDetailInfo),
    addToFavorites: () => { appStore.dispatch(addContentFavorite(projectDetailInfo.id, 'project')); },
    removeFromFavorites: () => { appStore.dispatch(removeContentFavorite(projectDetailInfo.id, projectDetailInfo.id, 'project', projectDetailInfo.name)); },
    sanitizer: $sanitize,
    telemetry: telemetryService,
  };

  // We need to update the current project ID in redux before the component mounts.
  // Otherwise, the sort order does not get applied properly.
  appStore.dispatch(enterSpace({
    item: projectDetailInfo,
    spaceType: 'project',
    subspaceName: undefined
  }));
}

Module.instance().controller('ProjectPlaceCtrl', ProjectPlaceCtrl);
